import React from "react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { AlertTriangle, CheckCircle2, HelpCircle } from "lucide-react";

/**
 * Single source of truth for the "Sjekk"-column.
 * Shows:
 *  - NB Nivå badge (red, clickable) when bulk flag has hits
 *  - OK Nivå badge (green) when bulk flag has 0 hits
 *  - "Ikke sjekket" muted badge when not yet bulk-checked
 *  - Two icon links to hockey.no and Elite Prospects, with tooltips
 */
export default function CheckCell({ player, flag, onShowDetail, testIdPrefix = "" }) {
  const checked = !!flag;
  const hasHits = checked && flag.hits > 0;
  const isClean = checked && flag.hits === 0;

  return (
    <TooltipProvider delayDuration={150}>
      <div className="flex items-center gap-2">
        {/* Status badge */}
        {hasHits ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                data-testid={`${testIdPrefix}nb-niva-${player.id}`}
                onClick={() => onShowDetail({ name: player.full_name, flag })}
                className="inline-flex items-center text-xs uppercase font-heading tracking-widest px-2 py-1 tag-rejected hover:opacity-80 cursor-pointer"
              >
                <AlertTriangle className="w-3 h-3 mr-1" />NB
              </button>
            </TooltipTrigger>
            <TooltipContent>{flag.hits} treff i øverste divisjon. Klikk for detaljer.</TooltipContent>
          </Tooltip>
        ) : isClean ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                data-testid={`${testIdPrefix}ok-niva-${player.id}`}
                className="inline-flex items-center text-xs uppercase font-heading tracking-widest px-2 py-1 tag-approved"
              >
                <CheckCircle2 className="w-3 h-3 mr-1" />OK
              </span>
            </TooltipTrigger>
            <TooltipContent>Sjekket mot hockey.no — ingen treff på øverste nivå</TooltipContent>
          </Tooltip>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex items-center text-xs uppercase font-heading tracking-widest px-2 py-1 border border-neutral-200 text-neutral-400">
                <HelpCircle className="w-3 h-3 mr-1" />?
              </span>
            </TooltipTrigger>
            <TooltipContent>Ikke sjekket — bruk "Sjekk alle" øverst</TooltipContent>
          </Tooltip>
        )}

        {/* External link – hockey.no */}
        <Tooltip>
          <TooltipTrigger asChild>
            <a
              href="https://live.hockey.no/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`${testIdPrefix}hno-${player.id}`}
              className="inline-flex items-center justify-center w-7 h-7 border border-neutral-200 hover:border-black text-neutral-700 hover:text-black font-heading font-black text-sm"
            >
              H
            </a>
          </TooltipTrigger>
          <TooltipContent>Sjekk hockey.no</TooltipContent>
        </Tooltip>

        {/* External link – Elite Prospects */}
        <Tooltip>
          <TooltipTrigger asChild>
            <a
              href={`https://www.eliteprospects.com/search/player?q=${encodeURIComponent(player.full_name)}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`${testIdPrefix}ep-${player.id}`}
              className="inline-flex items-center justify-center w-7 h-7 border border-neutral-200 hover:border-black text-neutral-700 hover:text-black font-heading font-black text-sm"
            >
              E
            </a>
          </TooltipTrigger>
          <TooltipContent>Sjekk elite prospects</TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
}
